import React, { useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './MobileFooterNav.css';

interface MobileFooterNavProps {
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

const MobileFooterNav: React.FC<MobileFooterNavProps> = ({ onMenuToggle, isSidebarOpen }) => {
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // Publishes the bar's height as --mobile-footer-nav-height, so what has to stop above it (the left
  // nav drawer) can. Measured rather than fixed: labels wrap to two lines on narrow phones, and the
  // bar includes the home-indicator inset. 0 on desktop, where the bar is display: none.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--mobile-footer-nav-height', `${nav.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return () => root.style.removeProperty('--mobile-footer-nav-height');
    const observer = new ResizeObserver(publish);
    observer.observe(nav);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--mobile-footer-nav-height');
    };
  }, []);

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <nav className="mobile-footer-nav" ref={navRef}>
      <div className="mobile-footer-nav-items">
        <button
          className={`mobile-footer-nav-item ${isSidebarOpen ? 'active' : ''}`}
          onClick={onMenuToggle}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
          <span>Menu</span>
        </button>
        <Link to="/agency-management/my-agency" className={`mobile-footer-nav-item ${isActive('/agency-management/my-agency') ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18"/>
            <path d="M5 21V7l7-4 7 4v14"/>
            <line x1="9" y1="10" x2="9" y2="10.01"/>
            <line x1="15" y1="10" x2="15" y2="10.01"/>
            <line x1="9" y1="14" x2="9" y2="14.01"/>
            <line x1="15" y1="14" x2="15" y2="14.01"/>
            <path d="M10 21v-3h4v3"/>
          </svg>
          <span>My Agency</span>
        </Link>
        <Link to="/assistant" className={`mobile-footer-nav-item ${isActive('/assistant') ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/>
            <path d="M19 15l.7 1.8 1.8.7-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7z"/>
          </svg>
          <span>Ask</span>
        </Link>
        <Link to="/calendar" className={`mobile-footer-nav-item ${isActive('/calendar') ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>Calendar</span>
        </Link>
        <Link to="/my-tickets" className={`mobile-footer-nav-item ${isActive('/my-tickets') ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
            <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
          </svg>
          <span>Support</span>
        </Link>
      </div>
    </nav>
  );
};

export default MobileFooterNav;
